import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RiArrowRightSLine } from "react-icons/ri";
import Select from "react-select";
import { getRequest, putRequest } from "../../helpers/api.helper";

const Step5Edit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { profile } = location.state || {};
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);

  // Add profile check
  if (!profile) {
    return (
      <div className="flex justify-center items-center h-screen text-red-600 font-semibold">
        ⚠️ Profile data not available.
      </div>
    );
  }

  /** ---------- State Management ---------- **/
  const [modalData, setModalData] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [city, setCity] = useState([]);
  const [occupation, setOccupation] = useState([]);

  const [formData, setFormData] = useState({
    totalBrothers: profile?.totalBrothers || "",
    marriedBrothers: profile?.marriedBrothers || "",
    totalSisters: profile?.totalSisters || "",
    marriedSisters: profile?.marriedSisters || "",
    familyValue: profile?.familyValue || "",
    familyType: profile?.familyType || "",
    youngerBrothers: profile?.youngerBrothers || "",
    elderBrothers: profile?.elderBrothers || "",
    youngerSisters: profile?.youngerSisters || "",
    elderSisters: profile?.elderSisters || "",
  });

  const [selectedOptions, setSelectedOptions] = useState<any>({
    Original_Location: profile?.originalLocation
      ? { value: profile.originalLocation, label: profile.originalLocation }
      : null,
    Setteled_Location: profile?.selectedLocation
      ? { value: profile.selectedLocation, label: profile.selectedLocation }
      : null,
    Father_Employee: profile?.fatherEmployee
      ? {
          value: profile.fatherEmployee,
          label: getEmploymentLabel(profile.fatherEmployee),
        }
      : null,
    Father_Occupation: profile?.fatherOccupied
      ? { value: profile.fatherOccupied, label: profile.fatherOccupied }
      : null,
    Mother_Employee: profile?.motherEmployee
      ? {
          value: profile.motherEmployee,
          label: getEmploymentLabel(profile.motherEmployee),
        }
      : null,
    Mother_Occupation: profile?.motherOccupied
      ? { value: profile.motherOccupied, label: profile.motherOccupied }
      : null,
  });

  // Helper function to get employment label
  function getEmploymentLabel(value: any) {
    const labels: any = {
      not_employee: "Not Employee",
      govt_employee: "Govt Employee",
      private_employee: "Private Employee",
      business_owner: "Business Owner",
      self_employe: "Self Employee",
      retired_employee: "Retired Employee",
      expaired: "Expired",
      farmer: "farmer",
      home_maker: "Home Maker",
    };
    return labels[value] || value;
  }

  /** ---------- Dropdown Options ---------- **/
  const dropdownOptions: any = {
    Father_Employee: {
      type: "single",
      options: [
        { value: "not_employee", label: "Not Employee" },
        { value: "govt_employee", label: "Govt Employee" },
        { value: "private_employee", label: "Private Employee" },
        { value: "business_owner", label: "Business Owner" },
        { value: "self_employe", label: "Self Employee" },
        { value: "retired_employee", label: "Retired Employee" },
        { value: "farmer", label: "Farmer" },
        { value: "expaired", label: "Expired" },
      ],
    },
    Father_Occupation: {
      type: "single",
      options: occupation.map((occ: any) => ({
        value: occ.occupation,
        label: occ.occupation,
      })),
    },
    Mother_Employee: {
      type: "single",
      options: [
        { value: "not_employee", label: "Not Employee" },
        { value: "govt_employee", label: "Govt Employee" },
        { value: "private_employee", label: "Private Employee" },
        { value: "business_owner", label: "Business Owner" },
        { value: "self_employe", label: "Self Employee" },
        { value: "retired_employee", label: "Retired Employee" },
        { value: "expaired", label: "Expired" },
        { value: "home_maker", label: "Home Maker" },
      ],
    },
    Mother_Occupation: {
      type: "single",
      options: occupation.map((occ: any) => ({
        value: occ.occupation,
        label: occ.occupation,
      })),
    },
    Original_Location: {
      type: "single",
      options: city.map((c: any) => ({ value: c.city, label: c.city })),
    },
    Setteled_Location: {
      type: "single",
      options: city.map((c: any) => ({ value: c.city, label: c.city })),
    },
  };

  /** ---------- Fetch Dropdown Data ---------- **/
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getRequest("/common/profile/step5");
        if (response.success) {
          setCity(response.data.city || []);
          setOccupation(response.data.occupation || []);
        }
      } catch (error) {
        console.error(`Error fetching data:`, error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!modalData && searchInputRef.current) {
      searchInputRef.current.blur();
    }
  }, [modalData]);

  /** ---------- Effects ---------- **/
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (modalData) {
      setSearchTerm("");
    }
  }, [modalData]);

  /** ---------- Handlers ---------- **/
  const openModal = (key: any) => {
    setModalData({
      key,
      type: dropdownOptions[key].type,
      options: dropdownOptions[key].options,
    });
  };

  const closeModal = () => {
    setModalData(null);
    setSearchTerm("");
  };

  const handleSingleSelect = (option: any) => {
    setSelectedOptions((prev: any) => ({
      ...prev,
      [modalData.key]: option,
    }));
    closeModal();
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const generateOptions = (maxValue: any) => {
    return [...Array(maxValue + 1).keys()].map((i) => (
      <option key={i} value={i}>
        {i}
      </option>
    ));
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const formDataToSend = {
      fatherEmployee: selectedOptions.Father_Employee?.value || "",
      fatherOccupied: selectedOptions.Father_Occupation?.value || "",
      motherEmployee: selectedOptions.Mother_Employee?.value || "",
      motherOccupied: selectedOptions.Mother_Occupation?.value || "",
      originalLocation: selectedOptions.Original_Location?.value || "",
      selectedLocation: selectedOptions.Setteled_Location?.value || "",
      totalBrothers: formData.totalBrothers,
      marriedBrothers: formData.marriedBrothers,
      totalSisters: formData.totalSisters,
      marriedSisters: formData.marriedSisters,
      familyValue: formData.familyValue,
      familyType: formData.familyType,
      youngerBrothers: formData.youngerBrothers,
      elderBrothers: formData.elderBrothers,
      youngerSisters: formData.youngerSisters,
      elderSisters: formData.elderSisters,
      step1: 1,
      step2: 1,
      step3: 1,
      step4: 1,
      step5: 1,
    };

    try {
      const response = await putRequest(
        `/bureau/profile/${profile._id}`,
        formDataToSend
      );

      if (response.success) {
        alert("Details updated successfully!");
        navigate(-1);
      } else {
        alert("Failed to update details.");
      }
    } catch (error) {
      console.error("Error updating details:", error);
      alert("An error occurred while updating details.");
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      width: "100%",
      padding: "0.75rem",
      borderRadius: "0.375rem",
      border: "1px solid #c4d4e2",
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: "#ffffff",
      zIndex: 9999,
    }),
    menuList: (provided: any) => ({
      ...provided,
      padding: 0,
      boxShadow: "0 20px 20px rgba(0, 0, 0, 0.9)",
      border: "2px solid #000000",
      borderRadius: "20px",
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      padding: "0.75rem",
      backgroundColor: state.isFocused ? "#f0f8ff" : "#ffffff",
      color: "#000",
      cursor: "pointer",
      borderBottom: "1px solid #c4d4e2",
      borderRadius: "13px",
    }),
  };

  /** ---------- Reusable Modal Component ---------- **/
  const Modal = () => {
    if (!modalData) return null;

    const filteredOptions = modalData.options.filter((option: any) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full shadow-lg m-5">
          <h2 className="text-lg font-bold mb-4 capitalize text-center">
            Select {modalData.key.replace(/_/g, " ")}
          </h2>

          {/* Search Bar */}
          <input
            ref={searchInputRef}
            type="text"
            autoFocus
            className="w-full px-4 py-2 mb-4 border border-gray-300 rounded focus:outline-none focus:ring focus:ring-blue-200"
            placeholder={`Search ${modalData.key.replace(/_/g, " ")}`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {/* Options List */}
          <div className="h-64 overflow-y-auto border-t border-b border-gray-200">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option: any, index: any) => (
                <React.Fragment key={option.value}>
                  <button
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 transition"
                    onClick={() => handleSingleSelect(option)}
                  >
                    {option.label}
                  </button>
                  {index < filteredOptions.length - 1 && (
                    <hr className="border-gray-200" />
                  )}
                </React.Fragment>
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">
                No options found
              </div>
            )}
          </div>

          {/* Close Button */}
          <button
            className="mt-4 w-full px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
            onClick={closeModal}
          >
            Continue
          </button>
        </div>
      </div>
    );
  };

  /** ---------- Render ---------- **/
  return (
    <div className="profile-edit-page flex justify-center items-center min-h-screen bg-gradient-to-r from-blue-50 to-red-100">
      <div className="w-full max-w-lg bg-white shadow-lg rounded-lg">
        {/* Header */}
        <div className="w-full z-50 bg-red-600 fixed top-0 mb-12 p-2 px-3 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <button className="flex items-center" onClick={handleBackClick}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h1 className="text-2xl font-bold text-center text-white py-3">
              Step 5 -
            </h1>
            <h5 className="font-semibold text-white">Family Details</h5>
          </div>
        </div>

        {/* Form */}
        <form
          className="flex flex-col gap-6 py-4 px-3 mt-20"
          onSubmit={handleSubmit}
        >
          {/* Modal Fields */}
          {Object.keys(dropdownOptions).map((key) => (
            <div key={key}>
              <label className="block text-red-500 font-medium mb-2">
                {key.replace(/_/g, " ")}
              </label>
              <div
                className="flex flex-col w-full border px-3 border-gray-300 py-3 rounded-lg cursor-pointer hover:shadow-lg transition-all"
                onClick={() => openModal(key)}
              >
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-md font-medium">
                    {selectedOptions[key]?.label ||
                      `Select ${key.replace(/_/g, " ")}`}
                  </span>
                  <RiArrowRightSLine className="text-3xl text-gray-500 ml-3" />
                </div>
              </div>
            </div>
          ))}

          {/* Total Brothers */}
          <div>
            <label
              htmlFor="totalBrothers"
              className="text-red-500 font-medium mb-2"
            >
              Total Brothers
            </label>
            <Select
              name="totalBrothers"
              value={
                formData.totalBrothers
                  ? {
                      label: formData.totalBrothers,
                      value: formData.totalBrothers,
                    }
                  : null
              }
              onChange={(selectedOption) =>
                setFormData({
                  ...formData,
                  totalBrothers: selectedOption ? selectedOption.value : "",
                  youngerBrothers: "",
                  elderBrothers: "",
                  marriedBrothers: "",
                })
              }
              options={[
                { value: "", label: "Select Total Brothers" },
                { value: "0", label: "No Brothers" },
                ...[...Array(10).keys()].map((i) => ({
                  value: (i + 1).toString(),
                  label: (i + 1).toString(),
                })),
              ]}
              placeholder="Search Total Brothers"
              styles={customStyles}
            />
          </div>

          {/* Younger and Elder Brothers */}
          {formData.totalBrothers && formData.totalBrothers !== "0" && (
            <>
              <div>
                <label
                  htmlFor="youngerBrothers"
                  className="text-red-500 font-medium mb-2"
                >
                  Number of Younger Brothers
                </label>
                <select
                  id="youngerBrothers"
                  name="youngerBrothers"
                  value={formData.youngerBrothers}
                  onChange={handleChange}
                  className="w-full p-4 border border-[#c4d4e2] rounded-md focus:outline-none focus:ring-2 focus:ring-[#c4d4e2] bg-white"
                >
                  <option value="">Select</option>
                  {generateOptions(parseInt(formData.totalBrothers))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="elderBrothers"
                  className="text-red-500 font-medium mb-2"
                >
                  Number of Elder Brothers
                </label>
                <select
                  id="elderBrothers"
                  name="elderBrothers"
                  value={formData.elderBrothers}
                  onChange={handleChange}
                  className="w-full p-4 border border-[#c4d4e2] rounded-md focus:outline-none focus:ring-2 focus:ring-[#c4d4e2] bg-white"
                >
                  <option value="">Select</option>
                  {generateOptions(parseInt(formData.totalBrothers))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="marriedBrothers"
                  className="text-red-500 font-medium mb-2"
                >
                  Married Brothers
                </label>
                <select
                  id="marriedBrothers"
                  name="marriedBrothers"
                  value={formData.marriedBrothers}
                  onChange={handleChange}
                  className="w-full p-4 border border-[#c4d4e2] rounded-md focus:outline-none focus:ring-2 focus:ring-[#c4d4e2] bg-white"
                >
                  <option value="">Select</option>
                  {generateOptions(parseInt(formData.totalBrothers))}
                </select>
              </div>
            </>
          )}

          {/* Total Sisters */}
          <div>
            <label
              htmlFor="totalSisters"
              className="text-red-500 font-medium mb-2"
            >
              Total Sisters
            </label>
            <Select
              name="totalSisters"
              value={
                formData.totalSisters
                  ? {
                      label: formData.totalSisters,
                      value: formData.totalSisters,
                    }
                  : null
              }
              onChange={(selectedOption) =>
                setFormData({
                  ...formData,
                  totalSisters: selectedOption ? selectedOption.value : "",
                  youngerSisters: "",
                  elderSisters: "",
                  marriedSisters: "",
                })
              }
              options={[
                { value: "", label: "Select Total Sisters" },
                { value: "0", label: "No Sisters" },
                ...[...Array(10).keys()].map((i) => ({
                  value: (i + 1).toString(),
                  label: (i + 1).toString(),
                })),
              ]}
              placeholder="Search Total Sisters"
              styles={customStyles}
            />
          </div>

          {/* Younger and Elder Sisters */}
          {formData.totalSisters && formData.totalSisters !== "0" && (
            <>
              <div>
                <label
                  htmlFor="youngerSisters"
                  className="text-red-500 font-medium mb-2"
                >
                  Number of Younger Sisters
                </label>
                <select
                  id="youngerSisters"
                  name="youngerSisters"
                  value={formData.youngerSisters}
                  onChange={handleChange}
                  className="w-full p-4 border border-[#c4d4e2] rounded-md focus:outline-none focus:ring-2 focus:ring-[#c4d4e2] bg-white"
                >
                  <option value="">Select</option>
                  {generateOptions(parseInt(formData.totalSisters))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="elderSisters"
                  className="text-red-500 font-medium mb-2"
                >
                  Number of Elder Sisters
                </label>
                <select
                  id="elderSisters"
                  name="elderSisters"
                  value={formData.elderSisters}
                  onChange={handleChange}
                  className="w-full p-4 border border-[#c4d4e2] rounded-md focus:outline-none focus:ring-2 focus:ring-[#c4d4e2] bg-white"
                >
                  <option value="">Select</option>
                  {generateOptions(parseInt(formData.totalSisters))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="marriedSisters"
                  className="text-red-500 font-medium mb-2"
                >
                  Married Sisters
                </label>
                <select
                  id="marriedSisters"
                  name="marriedSisters"
                  value={formData.marriedSisters}
                  onChange={handleChange}
                  className="w-full p-4 border border-[#c4d4e2] rounded-md focus:outline-none focus:ring-2 focus:ring-[#c4d4e2] bg-white"
                >
                  <option value="">Select</option>
                  {generateOptions(parseInt(formData.totalSisters))}
                </select>
              </div>
            </>
          )}

          {/* Family Value */}
          <div>
            <label
              htmlFor="familyValue"
              className="text-red-500 font-medium mb-2"
            >
              Family Value
            </label>
            <Select
              name="familyValue"
              value={
                formData.familyValue
                  ? { label: formData.familyValue, value: formData.familyValue }
                  : null
              }
              onChange={(selectedOption) =>
                setFormData({
                  ...formData,
                  familyValue: selectedOption ? selectedOption.value : "",
                })
              }
              options={[
                { value: "", label: "Select Family Value" },
                { value: "orthodox", label: "Orthodox" },
                { value: "traditional", label: "Traditional" },
              ]}
              placeholder="Search Family Value"
              styles={customStyles}
            />
          </div>

          {/* Family Type */}
          <div>
            <label
              htmlFor="familyType"
              className="text-red-500 font-medium mb-2"
            >
              Family Type
            </label>
            <Select
              name="familyType"
              value={
                formData.familyType
                  ? { label: formData.familyType, value: formData.familyType }
                  : null
              }
              onChange={(selectedOption) =>
                setFormData({
                  ...formData,
                  familyType: selectedOption ? selectedOption.value : "",
                })
              }
              options={[
                { value: "", label: "Select Family Type" },
                { value: "joint", label: "Joint" },
                { value: "nuclear", label: "Nuclear" },
              ]}
              placeholder="Select Family Type"
              styles={customStyles}
            />
          </div>

          <button
            type="submit"
            className="w-full mt-4 px-6 py-3 bg-red-600 text-white rounded-lg font-medium text-lg focus:outline-none hover:bg-red-700 transition"
          >
            Update Family Details
          </button>
        </form>

        {/* Reusable Modal */}
        <Modal />
      </div>
    </div>
  );
};

export default Step5Edit;
